import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Slide, SlideElement, Theme, ThemeFont, SlideBorderStyle } from '../types';

// GridLayoutItem 타입을 로컬에서 정의
interface GridLayoutItem {
  i: string;
  x: number;
  y: number;
  w: number;
  h: number;
}
import { RotateCw } from 'lucide-react';
import { generateColorMosaic, generateColorBlur } from '../utils/canvasEffects';
import { SafeEChartsRenderer } from './SafeEChartsRenderer';
import { SlideGridElements } from './SlideGridElements';
import { KeywordEditModal } from './KeywordEditModal';

interface SlideElementsProps {
  slide: Slide;
  theme?: Theme;
  themeFont?: ThemeFont;
  borderStyle?: SlideBorderStyle;
  isEditable?: boolean;
  selectedElementId?: string;
  selectedElementIds?: string[]; // 다중 선택 지원
  editingElementId?: string | null;
  onElementSelect?: (elementId: string, event?: MouseEvent | React.MouseEvent) => void;
  onElementUpdate?: (elementId: string, updates: Partial<SlideElement>) => void;
  onMultiElementUpdate?: (updates: Array<{id: string, updates: Partial<SlideElement>}>) => void; // 다중 업데이트
  onElementDelete?: (elementId: string) => void;
  onEditingElementChange?: (elementId: string | null) => void;
  onLayoutChange?: (layout: GridLayoutItem[]) => void;
  containerWidth?: number;
  containerHeight?: number;
  onSlideUpdate?: (updates: Partial<Slide>) => void; // 슬라이드 업데이트 (키워드 편집용)
  isDarkMode?: boolean;
  scale?: number; // 스케일 비율 추가
  disableTransformScale?: boolean; // 개별 요소에 스케일 적용 여부
}

export const SlideElements: React.FC<SlideElementsProps> = React.memo(({
  slide,
  theme,
  themeFont,
  borderStyle,
  isEditable = false,
  selectedElementId,
  selectedElementIds = [],
  editingElementId,
  onElementSelect,
  onElementUpdate,
  onMultiElementUpdate,
  onElementDelete,
  onEditingElementChange,
  onLayoutChange,
  containerWidth,
  containerHeight,
  onSlideUpdate,
  isDarkMode = false,
  scale = 1,
  disableTransformScale = false,
}) => {
  // 텍스트 스케일 계산: disableTransformScale이 true일 때만 scale 적용
  const textScale = disableTransformScale ? scale : 1;
  
  // All hooks must be declared at the top level
  // Commented out unused state to fix ESLint warning
  // const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  // const [elementStart, setElementStart] = useState({ x: 0, y: 0 });
  // const [draggedElement, setDraggedElement] = useState<string | null>(null);
  const [effectTextures, setEffectTextures] = useState<{[key: string]: string}>({});
  
  // 키워드 편집 모달 상태
  const [showKeywordModal, setShowKeywordModal] = useState(false);
  const [editingKeywords, setEditingKeywords] = useState<string[]>([]);

  // Memoize texture generation for pattern effects (moved before conditional return)
  const textureKeys = useMemo(() => {
    const keys: Array<{element: SlideElement, key: string}> = [];
    for (const element of slide.elements || []) {
      if (element.type === 'shape' && element.backgroundPattern) {
        keys.push({
          element,
          key: `${element.id}-${element.backgroundPattern}-${element.backgroundColor}`
        });
      }
    }
    return keys;
  }, [slide.elements]);

  // 요소의 패턴 효과를 위한 텍스처 생성
  useEffect(() => {
    const generateTextures = async () => {
      const newTextures: {[key: string]: string} = {};
      
      for (const { element, key: textureKey } of textureKeys) {
        if (!effectTextures[textureKey]) {
          try {
            let textureUrl = '';
            const color = element.backgroundColor || '#3B82F6';
            
            if (element.backgroundPattern === 'mosaic') {
              textureUrl = generateColorMosaic(color, element.width || 200, element.height || 100, 8);
            } else if (element.backgroundPattern === 'blur') {
              textureUrl = generateColorBlur(color, element.width || 200, element.height || 100, 5);
            }
            
            if (textureUrl) {
              newTextures[textureKey] = textureUrl;
            }
          } catch {  // Removed unused error variable
            // console.warn('Failed to generate texture for element', element.id);
          }
        }
      }
      
      if (Object.keys(newTextures).length > 0) {
        setEffectTextures(prev => ({ ...prev, ...newTextures }));
      }
    };

    generateTextures();
  }, [textureKeys, effectTextures]);

  // 키워드 편집 핸들러
  const handleKeywordEdit = useCallback(() => {
    if (slide.keywords && onSlideUpdate) {
      setEditingKeywords([...slide.keywords]);
      setShowKeywordModal(true);
    }
  }, [slide.keywords, onSlideUpdate]);

  const handleKeywordSave = useCallback((newKeywords: string[]) => {
    if (onSlideUpdate) {
      onSlideUpdate({ keywords: newKeywords });
    }
    setShowKeywordModal(false);
  }, [onSlideUpdate]);

  // 컨테이너 크기 메모이제이션 - 일관성 있는 값 반환 (moved before useEffect)
  const containerSize = useMemo(() => {
    if (containerWidth && containerHeight) {
      return { width: containerWidth, height: containerHeight };
    }
    // props가 없을 경우 기본값 사용 (DOM 탐색하지 않음)
    return { width: 1920, height: 1080 }; // 기본값
  }, [containerWidth, containerHeight]);

  // 키보드 이벤트 핸들러
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isEditable || !selectedElementId) {return;}
      
      
      const element = slide.elements?.find(el => el.id === selectedElementId);
      if (!element || element.locked) {return;}

      // containerSize는 이미 useMemo로 계산됨
      const gridUnit = containerSize.width * 0.01; // 1% 단위
      
      // 비율 기반 요소인지 확인
      const isRatioBased = element.x < 1 && element.y < 1 && (element.width || 0) < 1 && (element.height || 0) < 1;
      
      // 방향키 처리
      if (e.shiftKey) {
        // Shift + 방향키로 사이즈 조절
        switch (e.key) {
          case 'ArrowLeft': // 가로 사이즈 줄임
            e.preventDefault();
            if (isRatioBased) {
              const gridRatio = 0.01; // 1% 비율 단위
              onElementUpdate?.(selectedElementId, {
                width: Math.max(96 / containerSize.width, (element.width || 0.2) - gridRatio) // 최소 96px
              });
            } else {
              onElementUpdate?.(selectedElementId, {
                width: Math.max(96, (element.width || 200) - gridUnit) // 최소 96px
              });
            }
            break;
          case 'ArrowRight': // 가로 사이즈 늘림
            e.preventDefault();
            if (isRatioBased) {
              const gridRatio = 0.01; // 1% 비율 단위
              onElementUpdate?.(selectedElementId, {
                width: Math.min(1 - element.x, (element.width || 0.2) + gridRatio)
              });
            } else {
              onElementUpdate?.(selectedElementId, {
                width: Math.min(containerSize.width - element.x, (element.width || 200) + gridUnit)
              });
            }
            break;
          case 'ArrowUp': // 높이 줄임
            e.preventDefault();
            if (isRatioBased) {
              const gridRatio = 0.01; // 1% 비율 단위
              onElementUpdate?.(selectedElementId, {
                height: Math.max(50 / containerSize.height, (element.height || 0.1) - gridRatio) // 최소 50px
              });
            } else {
              onElementUpdate?.(selectedElementId, {
                height: Math.max(50, (element.height || 100) - gridUnit) // 최소 50px
              });
            }
            break;
          case 'ArrowDown': // 높이 늘림
            e.preventDefault();
            if (isRatioBased) {
              const gridRatio = 0.01; // 1% 비율 단위
              onElementUpdate?.(selectedElementId, {
                height: Math.min(1 - element.y, (element.height || 0.1) + gridRatio)
              });
            } else {
              onElementUpdate?.(selectedElementId, {
                height: Math.min(containerSize.height - element.y, (element.height || 100) + gridUnit)
              });
            }
            break;
        }
      } else {
        // 일반 방향키로 위치 이동
        switch (e.key) {
          case 'ArrowLeft':
            e.preventDefault();
            if (isRatioBased) {
              const gridRatio = 0.01; // 1% 비율 단위
              // 현재 위치를 1% 그리드에 맞춘 후 이동
              const snappedX = Math.floor(element.x / gridRatio) * gridRatio;
              const newX = Math.max(0, snappedX - gridRatio);
              onElementUpdate?.(selectedElementId, { x: newX });
            } else {
              const newX = Math.max(0, element.x - gridUnit);
              onElementUpdate?.(selectedElementId, { x: newX });
            }
            break;
          case 'ArrowRight':
            e.preventDefault();
            if (isRatioBased) {
              const gridRatio = 0.01; // 1% 비율 단위
              // 현재 위치를 1% 그리드에 맞춘 후 이동
              const snappedX = Math.ceil(element.x / gridRatio) * gridRatio;
              const maxX = 1 - (element.width || 0.2);
              const newX = Math.min(maxX, snappedX + gridRatio);
              onElementUpdate?.(selectedElementId, { x: newX });
            } else {
              const maxX = containerSize.width - (element.width || 200);
              const newX = Math.min(maxX, element.x + gridUnit);
              onElementUpdate?.(selectedElementId, { x: newX });
            }
            break;
          case 'ArrowUp':
            e.preventDefault();
            if (isRatioBased) {
              const gridRatio = 0.01; // 1% 비율 단위
              // 현재 위치를 1% 그리드에 맞춘 후 이동
              const snappedY = Math.floor(element.y / gridRatio) * gridRatio;
              const newY = Math.max(0, snappedY - gridRatio);
              onElementUpdate?.(selectedElementId, { y: newY });
            } else {
              const newY = Math.max(0, element.y - gridUnit);
              onElementUpdate?.(selectedElementId, { y: newY });
            }
            break;
          case 'ArrowDown':
            e.preventDefault();
            if (isRatioBased) {
              const gridRatio = 0.01; // 1% 비율 단위
              // 현재 위치를 1% 그리드에 맞춘 후 이동
              const snappedY = Math.ceil(element.y / gridRatio) * gridRatio;
              const maxY = 1 - (element.height || 0.1);
              const newY = Math.min(maxY, snappedY + gridRatio);
              onElementUpdate?.(selectedElementId, { y: newY });
            } else {
              const maxY = containerSize.height - (element.height || 100);
              const newY = Math.min(maxY, element.y + gridUnit);
              onElementUpdate?.(selectedElementId, { y: newY });
            }
            break;
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isEditable, selectedElementId, slide.elements, onElementUpdate, containerSize]);

  // 그리드 스냅 함수 (비율 기준 0-1) - 더 부드러운 움직임을 위해 0.5% 단위로 변경 (moved before conditional return)
  const snapToGrid = useCallback((value: number, enableSnap = true) => {
    if (!enableSnap) {return value;}
    const gridUnit = 0.005; // 0.5% 단위로 더 부드럽게
    return Math.round(value / gridUnit) * gridUnit;
  }, []);

  // 슬라이드 컨테이너 크기 가져오기 (레거시 함수 - containerSize 사용 권장) (moved before conditional return)
  // const getSlideContainerSize = useCallback(() => {
  //   return containerSize;
  // }, [containerSize]);

  const handleMouseDown = useCallback((e: React.MouseEvent, elementId: string, action: 'move' | 'resize' | 'rotate', resizeDirection?: 'se' | 'ne' | 'sw' | 'nw' | 'n' | 's' | 'e' | 'w') => {
    if (!isEditable) {return;}
    
    e.preventDefault();
    e.stopPropagation();
    
    const element = slide.elements?.find(el => el.id === elementId);
    if (!element) {return;}

    // 현재 요소가 속한 슬라이드 렌더러 컨테이너를 찾기
    // 가장 가까운 슬라이드 렌더러 컨테이너를 찾되, 편집 모드의 컨테이너를 우선적으로 찾기
    let targetElement = e.currentTarget as HTMLElement;
    let slideContainer: HTMLElement | null = null;
    
    // 부모 요소를 순회하며 슬라이드 렌더러 컨테이너 찾기
    while (targetElement && !slideContainer) {
      if (targetElement.classList.contains('slide-renderer-container')) {
        slideContainer = targetElement;
        break;
      }
      targetElement = targetElement.parentElement as HTMLElement;
    }
    
    const slideRect = slideContainer?.getBoundingClientRect();
    if (!slideRect) {return;}

    // 슬라이드 화면 기준 마우스 시작 위치 (0-1 비율)
    const startMouseX = (e.clientX - slideRect.left) / slideRect.width;
    const startMouseY = (e.clientY - slideRect.top) / slideRect.height;
    
    // 모든 좌표를 0-1 비율로 정규화 - 항상 0-1 범위로 가정
    const startX = element.x || 0;
    const startY = element.y || 0;
    const startWidth = element.width ?? 0.2;
    const startHeight = element.height ?? 0.2;

    const handleMouseMove = (e: MouseEvent) => {
      // 처음에 찾은 슬라이드 컨테이너의 현재 위치를 사용
      const currentSlideRect = slideContainer?.getBoundingClientRect();
      if (!currentSlideRect) {return;}

      // 현재 마우스 위치를 슬라이드 화면 기준 0-1 비율로 변환
      const currentMouseX = Math.max(0, Math.min((e.clientX - currentSlideRect.left) / currentSlideRect.width, 1));
      const currentMouseY = Math.max(0, Math.min((e.clientY - currentSlideRect.top) / currentSlideRect.height, 1));
      
      // 비율 기준 델타 계산
      const deltaX = currentMouseX - startMouseX;
      const deltaY = currentMouseY - startMouseY;

      if (action === 'move') {
        // 다중 선택된 요소들이 있고 현재 요소가 그 중 하나인 경우 다중 이동
        const isMultiMove = selectedElementIds.includes(elementId) && selectedElementIds.length > 1;
        
        if (isMultiMove && onMultiElementUpdate) {
          // 다중 요소 이동
          const updates = selectedElementIds.map(id => {
            const el = slide.elements?.find(e => e.id === id);
            if (!el) {return null;}
            
            // 모든 좌표를 0-1 비율로 정규화 - 항상 0-1 범위로 가정
            const elX = el.x || 0;
            const elY = el.y || 0;
            const elWidth = el.width || 0.2;
            const elHeight = el.height || 0.2;
            
            // 새 위치 계산 (슬라이드 내부에 제한, 부드러운 그리드 스냅)
            const newX = snapToGrid(Math.max(0, Math.min(elX + deltaX, 1 - elWidth)), e.shiftKey);
            const newY = snapToGrid(Math.max(0, Math.min(elY + deltaY, 1 - elHeight)), e.shiftKey);
            
            return { id, updates: { x: newX, y: newY } };
          }).filter(Boolean) as Array<{id: string, updates: Partial<SlideElement>}>;
          
          onMultiElementUpdate(updates);
        } else {
          // 단일 요소 이동 - 슬라이드 내부에 제한, 부드러운 그리드 스냅
          const newX = snapToGrid(Math.max(0, Math.min(startX + deltaX, 1 - startWidth)), e.shiftKey);
          const newY = snapToGrid(Math.max(0, Math.min(startY + deltaY, 1 - startHeight)), e.shiftKey);
          
          onElementUpdate?.(elementId, { x: newX, y: newY });
        }
      } else if (action === 'resize') {
        const direction = resizeDirection || 'se'; // 기본값은 남동쪽
        let newX = startX;
        let newY = startY;
        let newWidth = startWidth;
        let newHeight = startHeight;
        
        // 최소 크기 (5% 너비, 3% 높이)
        const minWidth = 0.05;
        const minHeight = 0.03;
        
        // 방향에 따른 크기 및 위치 조절 (모든 좌표 0-1 비율)
        switch (direction) {
          case 'se': // 남동쪽 (오른쪽 아래)
            newWidth = Math.max(minWidth, Math.min(startWidth + deltaX, 1 - startX));
            newHeight = Math.max(minHeight, Math.min(startHeight + deltaY, 1 - startY));
            break;
          case 'ne': // 북동쪽 (오른쪽 위)
            newWidth = Math.max(minWidth, Math.min(startWidth + deltaX, 1 - startX));
            newHeight = Math.max(minHeight, startHeight - deltaY);
            newY = Math.max(0, Math.min(startY + deltaY, startY + startHeight - minHeight));
            break;
          case 'sw': // 남서쪽 (왼쪽 아래)
            newWidth = Math.max(minWidth, startWidth - deltaX);
            newHeight = Math.max(minHeight, Math.min(startHeight + deltaY, 1 - startY));
            newX = Math.max(0, Math.min(startX + deltaX, startX + startWidth - minWidth));
            break;
          case 'nw': // 북서쪽 (왼쪽 위)
            newWidth = Math.max(minWidth, startWidth - deltaX);
            newHeight = Math.max(minHeight, startHeight - deltaY);
            newX = Math.max(0, Math.min(startX + deltaX, startX + startWidth - minWidth));
            newY = Math.max(0, Math.min(startY + deltaY, startY + startHeight - minHeight));
            break;
          case 'n': // 북쪽 (위)
            newHeight = Math.max(minHeight, startHeight - deltaY);
            newY = Math.max(0, Math.min(startY + deltaY, startY + startHeight - minHeight));
            break;
          case 's': // 남쪽 (아래)
            newHeight = Math.max(minHeight, Math.min(startHeight + deltaY, 1 - startY));
            break;
          case 'e': // 동쪽 (오른쪽)
            newWidth = Math.max(minWidth, Math.min(startWidth + deltaX, 1 - startX));
            break;
          case 'w': // 서쪽 (왼쪽)
            newWidth = Math.max(minWidth, startWidth - deltaX);
            newX = Math.max(0, Math.min(startX + deltaX, startX + startWidth - minWidth));
            break;
        }
        
        // 1% 그리드 스냅 적용
        const updateData: Partial<SlideElement> = { 
          width: snapToGrid(newWidth), 
          height: snapToGrid(newHeight) 
        };
        if (newX !== startX) {updateData.x = snapToGrid(newX);}
        if (newY !== startY) {updateData.y = snapToGrid(newY);}
        
        onElementUpdate?.(elementId, updateData);
      } else if (action === 'rotate') {
        // 회전 각도 계산 (요소 중심 기준, 0-1 비율 좌표)
        const elementCenterX = startX + startWidth / 2;
        const elementCenterY = startY + startHeight / 2;
        
        const angle = Math.atan2(currentMouseY - elementCenterY, currentMouseX - elementCenterX) * (180 / Math.PI);
        onElementUpdate?.(elementId, { rotation: angle });
      }
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [isEditable, slide.elements, snapToGrid, selectedElementIds, onMultiElementUpdate, onElementUpdate, containerSize, scale]);

  const renderElement = useCallback((element: SlideElement) => {
    const isSelected = selectedElementId === element.id;
    const isEditing = editingElementId === element.id;
    
    // 숨겨진 요소는 렌더링하지 않음 (편집 모드에서는 반투명하게 표시)
    if (element.visible === false && !isEditable) {
      return null;
    }

    // containerSize는 이미 useMemo로 계산됨
    
    // 요소가 비율 기반인지 확인 (구조화된 요소들)
    const isRatioBased = element.x < 1 && element.y < 1 && (element.width ?? 0) < 1 && (element.height ?? 0) < 1;
    
    // 슬라이드 테마에 따른 콘텐츠 영역 조정
    // const getContentAreaBounds = () => {
    //   const slideTheme = slide.slideTheme;
    //   if (!slideTheme || slideTheme === 'default') {
    //     return { offsetX: 0, offsetY: 0, scaleX: 1, scaleY: 1 };
    //   }
    //   
    //   // 동적 분할 위치 (기본값: 50%)
    //   const splitPos = slide.splitPosition || 50
    //   const splitRatio = splitPos / 100;
    //   const complementRatio = 1 - splitRatio;
    //   
    //   switch (slideTheme) {
    //     case 'left-split':
    //       return { offsetX: containerSize.width * splitRatio, offsetY: 0, scaleX: complementRatio, scaleY: 1 };
    //     case 'right-split':
    //       return { offsetX: 0, offsetY: 0, scaleX: complementRatio, scaleY: 1 };
    //     case 'top-split':
    //       return { offsetX: 0, offsetY: containerSize.height * splitRatio, scaleX: 1, scaleY: complementRatio };
    //     case 'bottom-split':
    //       return { offsetX: 0, offsetY: 0, scaleX: 1, scaleY: complementRatio };
    //     case 'diagonal':
    //       return { offsetX: containerSize.width * (0.3 + (splitRatio - 0.5)), offsetY: 0, scaleX: 0.7 + (0.5 - splitRatio), scaleY: 1 };
    //     default:
    //       return { offsetX: 0, offsetY: 0, scaleX: 1, scaleY: 1 };
    //   }
    // };
    
    // const contentBounds = getContentAreaBounds();
    
    // 비율 기반일 경우 실제 픽셀로 변환하되 콘텐츠 영역에 맞게 조정
    const actualX = isRatioBased ? element.x * containerSize.width : element.x;
    const actualY = isRatioBased ? element.y * containerSize.height : element.y;
    const actualWidth = isRatioBased ? (element.width ?? 0.2) * containerSize.width : (element.width ?? 200);
    const actualHeight = isRatioBased ? (element.height ?? 0.1) * containerSize.height : (element.height ?? 100);
    
    // 슬라이드 테마가 적용된 경우에도 전체 영역에 배치 가능
    // Content area bounds are now only used as guidelines, not restrictions
    // if (slide.slideTheme && slide.slideTheme !== 'default') {
    //   actualX = contentBounds.offsetX + (actualX * contentBounds.scaleX);
    //   actualY = contentBounds.offsetY + (actualY * contentBounds.scaleY);
    // }

    return (
      <div
        key={`${element.id}-${borderStyle?.id || 'none'}`}
        className={`slide-element ${isEditable ? 'cursor-move' : ''}`}
        style={{
          position: 'absolute',
          left: `${actualX}px`,
          top: `${actualY}px`,
          width: `${actualWidth}px`,
          height: `${actualHeight}px`,
          transform: (() => {
            let transform = `rotate(${element.rotation || 0}deg)`;
            // 포스트잇 스타일일 때 랜덤 각도 적용
            if (borderStyle?.id === 'postit') {
              // 글 내용을 기반으로 일관된 랜덤 각도 생성
              const content = element.content || '';
              const seed = content.length + content.charCodeAt(0) || 0;
              const randomAngle = (Math.sin(seed) * 0.5 + 0.5) * 6 - 3; // -3도 ~ +3도
              transform += ` rotate(${randomAngle}deg) scale(0.99)`;
            }
            return transform;
          })(),
          zIndex: Math.min(element.zIndex || 1, 10), // 최대 z-index를 10으로 제한
          outline: isSelected && isEditable ? '2px solid #3B82F6' : 'none',
          outlineOffset: '2px',
          opacity: element.visible === false ? 0.3 : 1,
          pointerEvents: element.locked ? 'none' : 'auto',
          // 테두리 스타일 적용
          border: (() => {
            if (!borderStyle) {return 'none';}
            
            // 액자 스타일인 경우 모던한 액자 테두리 적용
            if (borderStyle.id === 'frame' && theme) {
              const frameBorders = [
                '2px solid #333', // 미니멀 블랙
                '3px solid #666', // 미니멀 그레이
                '1px solid #000', // 울트라 미니멀
                '4px solid #444', // 모던 그레이
              ];
              const randomIndex = Math.floor(Math.random() * frameBorders.length);
              return frameBorders[randomIndex];
            }
            
            // 다른 스타일들은 기존 로직 사용
            return `${borderStyle.borderWidth}px ${borderStyle.borderStyle} ${borderStyle.id === 'bold-frame' && theme ? theme.accent : '#E5E7EB'}`;
          })(),
          borderRadius: (() => {
            if (borderStyle?.id === 'frame') {
              // 액자 스타일일 때는 라운드 제거
              return '0px';
            }
            return borderStyle ? `${borderStyle.borderRadius}px` : element.borderRadius || 0;
          })(),
          boxShadow: (() => {
            if (borderStyle?.id === 'postit') {
              // 포스트잇 스타일일 때 그림자를 우측과 하단으로 강조
              return '15px 15px 30px rgba(0,0,0,0.6)';
            }
            if (borderStyle?.id === 'frame' && theme) {
              // 액자 스타일일 때 모던한 그림자 효과
              const frameShadows = [
                '0 0 0 10px white, 0 0 0 12px #333, 0 10px 30px rgba(0,0,0,0.2)', // 미니멀 이중 프레임
                '0 0 0 8px white, 0 0 0 10px #666, 0 8px 24px rgba(0,0,0,0.15)', // 모던 그레이
                '0 0 0 6px white, 0 0 0 7px #000, 0 6px 20px rgba(0,0,0,0.1)', // 울트라 미니멀
                '0 0 0 12px white, 0 0 0 14px #444, 0 12px 32px rgba(0,0,0,0.25)', // 모던 그레이
              ];
              const randomIndex = Math.floor(Math.random() * frameShadows.length);
              return frameShadows[randomIndex];
            }
            return borderStyle?.boxShadow || element.boxShadow || 'none';
          })(),
          // 박스 스타일 배경 적용 (특수 스타일 제외)
          ...(borderStyle && theme && !['bullet', 'burnt'].includes(borderStyle.id) && {
            background: (() => {
              // 액자 스타일인 경우 흰색 배경으로 통일
              if (borderStyle.id === 'frame') {
                return 'white';
              }
              
              // 포스트잇 스타일인 경우 노란색 배경
              if (borderStyle.id === 'postit') {
                return '#fffacd';
              }
              
              // 다른 스타일들은 기존 backgroundColor 사용
              return borderStyle.backgroundColor || 'transparent';
            })()
          }),
          // 글래스모픽 스타일 적용
          ...(borderStyle?.id === 'glassmorphic' && {
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px) saturate(180%)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
          }),
          // 불릿 스타일 적용
          ...(borderStyle?.id === 'bullet' && {
            background: theme?.quaternary ? 
              `linear-gradient(135deg, ${theme.primary}, ${theme.quaternary})` : 
              'black',
            backdropFilter: 'none',
            border: 'none',
            borderRadius: '0 50px 50px 0', // 오른쪽만 둥글게
            boxShadow: 'none',
          }),
          // 그을림 스타일 적용
          ...(borderStyle?.id === 'burnt' && {
            background: (() => {
              // 검은색~짙은 회색 계열 5가지 색상
              const darkColors = [
                '#000000', // 순수 검은색
                '#1a1a1a', // 매우 짙은 회색
                '#2d2d2d', // 짙은 회색
                '#404040', // 중간 짙은 회색
                '#525252'  // 짙은 회색
              ];
              
              // 요소 ID 기반으로 일관성 유지 (수정 시에만 변경)
              const colorSeed = element.id.charCodeAt(0) + element.id.length;
              const selectedColor = darkColors[colorSeed % darkColors.length];
              
              // 네 방향으로 투명하게 하는 그라데이션 (알파값 사용)
              return `radial-gradient(ellipse at center, ${selectedColor} 0%, ${selectedColor} 50%, ${selectedColor}EE 70%, ${selectedColor}CC 85%, ${selectedColor}99 95%, ${selectedColor}66 97%, transparent 100%)`;
            })(),
            backdropFilter: 'brightness(1) contrast(1.5) sepia(1)',
            border: 'none !important',
            borderRadius: '0',
            // 테두리 그을린 그라데이션 boxShadow
            boxShadow: `
              inset 0 0 0 1px rgba(0,0,0,0.8),
              inset 0 0 0 2px rgba(139,69,19,0.6),
              inset 0 0 0 3px rgba(160,82,45,0.4),
              inset 0 0 0 4px rgba(205,133,63,0.2),
              0 4px 12px rgba(0,0,0,0.3)
            `,
            // 불탄 느낌의 거친 테두리
            clipPath: (() => {
              // 비율에 따른 깊이 보정
              const aspectRatio = (element.width ?? 200) / (element.height ?? 100);
              const isWide = aspectRatio > 1.5; // 가로가 긴 경우
              const isTall = aspectRatio < 0.67; // 세로가 긴 경우
              
              // 비율에 따른 깊이 조정 (더 얕게)
              const horizontalDepth = isWide ? 0.8 : isTall ? 2.5 : 1.8; // 가로가 길면 가로 깊이 얕게
              const verticalDepth = isWide ? 3.5 : isTall ? 0.8 : 1.8; // 가로가 길면 세로 깊이 깊게
              
              // 4가지 모양 중 랜덤 선택 (요소 ID 기반으로 일관성 유지)
              const shapeSeed = element.id.charCodeAt(0) + element.id.length;
              const shapeType = shapeSeed % 4; // 0, 1, 2, 3
              
              const points = [];
              
              // 상단 테두리 (400개 점)
              for (let i = 0; i <= 400; i++) {
                const x = (i / 400) * 100;
                let y;
                
                switch (shapeType) {
                  case 0: // 둥근 계단형
                    y = Math.round(Math.sin(i * 0.3) * horizontalDepth * 2) * 0.5;
                    break;
                  case 1: // 부드러운 계단형
                    y = Math.round(Math.sin(i * 0.4) * horizontalDepth * 1.5 + Math.sin(i * 0.1) * horizontalDepth * 0.5) * 0.3;
                    break;
                  case 2: // 불규칙 계단형
                    y = Math.round(Math.sin(i * 0.25) * horizontalDepth * 2.5 + Math.sin(i * 0.08) * horizontalDepth * 1.2) * 0.4;
                    break;
                  case 3: // 복합 계단형
                    y = Math.round(Math.sin(i * 0.35) * horizontalDepth * 1.8 + Math.sin(i * 0.15) * horizontalDepth * 0.8 + Math.sin(i * 0.05) * horizontalDepth * 0.4) * 0.35;
                    break;
                  default:
                    y = Math.round(Math.sin(i * 0.3) * horizontalDepth * 2) * 0.5;
                }
                points.push(`${x}% ${y}%`);
              }
              
              // 우측 테두리 (240개 점)
              for (let i = 0; i <= 240; i++) {
                const y = (i / 240) * 100;
                let x;
                
                switch (shapeType) {
                  case 0: // 둥근 계단형
                    x = 100 - Math.round(Math.sin(i * 0.3) * verticalDepth * 2) * 0.5;
                    break;
                  case 1: // 부드러운 계단형
                    x = 100 - Math.round(Math.sin(i * 0.4) * verticalDepth * 1.5 + Math.sin(i * 0.1) * verticalDepth * 0.5) * 0.3;
                    break;
                  case 2: // 불규칙 계단형
                    x = 100 - Math.round(Math.sin(i * 0.25) * verticalDepth * 2.5 + Math.sin(i * 0.08) * verticalDepth * 1.2) * 0.4;
                    break;
                  case 3: // 복합 계단형
                    x = 100 - Math.round(Math.sin(i * 0.35) * verticalDepth * 1.8 + Math.sin(i * 0.15) * verticalDepth * 0.8 + Math.sin(i * 0.05) * verticalDepth * 0.4) * 0.35;
                    break;
                  default:
                    x = 100 - Math.round(Math.sin(i * 0.3) * verticalDepth * 2) * 0.5;
                }
                points.push(`${x}% ${y}%`);
              }
              
              // 하단 테두리 (400개 점)
              for (let i = 400; i >= 0; i--) {
                const x = (i / 400) * 100;
                let y;
                
                switch (shapeType) {
                  case 0: // 둥근 계단형
                    y = 100 - Math.round(Math.sin(i * 0.3) * horizontalDepth * 2) * 0.5;
                    break;
                  case 1: // 부드러운 계단형
                    y = 100 - Math.round(Math.sin(i * 0.4) * horizontalDepth * 1.5 + Math.sin(i * 0.1) * horizontalDepth * 0.5) * 0.3;
                    break;
                  case 2: // 불규칙 계단형
                    y = 100 - Math.round(Math.sin(i * 0.25) * horizontalDepth * 2.5 + Math.sin(i * 0.08) * horizontalDepth * 1.2) * 0.4;
                    break;
                  case 3: // 복합 계단형
                    y = 100 - Math.round(Math.sin(i * 0.35) * horizontalDepth * 1.8 + Math.sin(i * 0.15) * horizontalDepth * 0.8 + Math.sin(i * 0.05) * horizontalDepth * 0.4) * 0.35;
                    break;
                  default:
                    y = 100 - Math.round(Math.sin(i * 0.3) * horizontalDepth * 2) * 0.5;
                }
                points.push(`${x}% ${y}%`);
              }
              
              // 좌측 테두리 (240개 점)
              for (let i = 240; i >= 0; i--) {
                const y = (i / 240) * 100;
                let x;
                
                switch (shapeType) {
                  case 0: // 둥근 계단형
                    x = Math.round(Math.sin(i * 0.3) * verticalDepth * 2) * 0.5;
                    break;
                  case 1: // 부드러운 계단형
                    x = Math.round(Math.sin(i * 0.4) * verticalDepth * 1.5 + Math.sin(i * 0.1) * verticalDepth * 0.5) * 0.3;
                    break;
                  case 2: // 불규칙 계단형
                    x = Math.round(Math.sin(i * 0.25) * verticalDepth * 2.5 + Math.sin(i * 0.08) * verticalDepth * 1.2) * 0.4;
                    break;
                  case 3: // 복합 계단형
                    x = Math.round(Math.sin(i * 0.35) * verticalDepth * 1.8 + Math.sin(i * 0.15) * verticalDepth * 0.8 + Math.sin(i * 0.05) * verticalDepth * 0.4) * 0.35;
                    break;
                  default:
                    x = Math.round(Math.sin(i * 0.3) * verticalDepth * 2) * 0.5;
                }
                points.push(`${x}% ${y}%`);
              }
              return `polygon(${points.join(', ')})`;
            })(),
          }),
          padding: borderStyle?.padding ? `${borderStyle.padding}px` : undefined,
          // 즉석 사진 스타일일 때 비율에 따라 스타일 적용
          ...(borderStyle?.id === 'polaroid' && (() => {
            const aspectRatio = element.width / element.height;
            const isHorizontal = aspectRatio > 0.8; // 가로가 세로의 1.5배 이상
            const isVertical = aspectRatio < 0.45; // 세로가 가로의 1.5배 이상 (1/1.5 = 0.67)
            
            // 가로나 세로로 길면 스티커 스타일, 그렇지 않으면 즉석사진 스타일
            if (isHorizontal || isVertical) {
              // 스티커 색상을 랜덤하게 선택 (테마의 3번째, 4번째 색상)
              const stickerColors = [theme?.primary, theme?.quaternary].filter(Boolean);
              const randomStickerColor = stickerColors[Math.floor(Math.random() * stickerColors.length)] || theme.primary;
              
              // 스티커 스타일 적용 (오돌토돌한 디자인 포함)
              return {
                backgroundColor: `${randomStickerColor}100`, // 투명도를 80에서 60으로 줄임
                border: '2px solid rgba(0,0,0,0.1)',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15), 0 1px 3px rgba(0,0,0,0.1), 0 0 0 2px rgba(0,0,0,0.05)',
                backdropFilter: 'contrast(1.05) brightness(0.9) saturate(0.8)',
                filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.1)) brightness(0.85) contrast(1.1)',
                // 오돌토돌한 스티커 자른 느낌
                clipPath: (() => {
                  const points = [];
                  // 좌측 오돌토돌 (100개 점)
                  for (let i = 0; i <= 100; i++) {
                    const y = (i / 100) * 95;
                    const x = Math.sin(i * 1) * 0.5;
                    points.push(`${x}% ${y}%`);
                  }
                  // 우측 오돌토돌 (100개 점)
                  for (let i = 100; i >= 0; i--) {
                    const y = (i / 90) * 90;
                    const x = 100 - Math.sin(i * 1) * 0.5;
                    points.push(`${x}% ${y}%`);
                  }
                  return `polygon(${points.join(', ')})`;
                })(),
              };
            } else {
              // 즉석사진 스타일 적용
              return {
                paddingBottom: '40px',
                border: '10px solid #f0f0f0',
                borderRadius: '0',
                background: 'linear-gradient(to bottom, rgba(150, 130, 110, 0.25) 0%, rgba(130, 110, 150, 0.15) calc(100% - 40px), #f0f0f0 calc(100% - 40px), #f0f0f0 100%)',
                backdropFilter: 'sepia(0.2) brightness(1.1) contrast(0.9) saturate(1.3) hue-rotate(5deg)',
                filter: 'grayscale(1) contrast(1.1) brightness(0.95) contrast(1.1) saturate(1.2) brightness(1.05)',
              };
            }
          })()),
          // 제목 객체에 스티커 느낌 적용
          ...(borderStyle?.id === 'polaroid' && element.y <= 0.1 && theme && {
            backgroundColor: `${theme?.accent || theme?.primary || '#3B82F6'}95`, // 테마 액센트 색상에 80% 투명도
            border: '1px solid rgba(0,0,0,0.1)',
            borderRadius: '2px',
            backdropFilter: 'contrast(1.05) brightness(1.02)',
            filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.1))',
            // 200개의 매우 미세한 오돌토돌한 점들로 스티커 자른 느낌 만들기
            clipPath: (() => {
              const points = [];
              // 좌측 오돌토돌 (100개 점)
              for (let i = 0; i <= 100; i++) {
                const y = (i / 100) * 95;
                const x = Math.sin(i * 1) * 0.5; // 스케일을 10분의 1로 축소 (2 → 0.2)
                points.push(`${x}% ${y}%`);
              }
              // 우측 오돌토돌 (100개 점)
              for (let i = 100; i >= 0; i--) {
                const y = (i / 90) * 90;
                const x = 100 - Math.sin(i * 1) * 0.5; // 스케일을 10분의 1로 축소
                points.push(`${x}% ${y}%`);
              }
              return `polygon(${points.join(', ')})`;
            })(),
          }),
        }}
        onClick={(e) => {
          if (isEditable) {
            e.stopPropagation();
            onElementSelect?.(element.id, e);
          }
        }}
        onDoubleClick={(e) => {
          if (isEditable && element.type === 'text') {
            e.stopPropagation();
            onEditingElementChange?.(element.id);
          }
        }}
        onMouseDown={(e) => {
          if (isEditable && !isEditing) {
            e.stopPropagation();
            onElementSelect?.(element.id, e);
            // 마우스 드래그로 이동 시작
            handleMouseDown(e, element.id, 'move');
          }
        }}
      >
        {/* 선택된 요소의 컨트롤 핸들 */}
        {isSelected && isEditable && !isEditing && (
          <div className="absolute inset-0 pointer-events-none">
            {/* 8방향 리사이즈 핸들 */}
            {/* 모서리 핸들 */}
            <div
              className="absolute -top-2 -left-2 w-4 h-4 bg-blue-500 rounded-full cursor-nw-resize z-50 pointer-events-auto shadow-lg hover:bg-blue-600 transition-colors"
              onMouseDown={(e) => {
                e.stopPropagation();
                handleMouseDown(e, element.id, 'resize', 'nw');
              }}
            />
            <div
              className="absolute -top-2 -right-2 w-4 h-4 bg-blue-500 rounded-full cursor-ne-resize z-50 pointer-events-auto shadow-lg hover:bg-blue-600 transition-colors"
              onMouseDown={(e) => {
                e.stopPropagation();
                handleMouseDown(e, element.id, 'resize', 'ne');
              }}
            />
            <div
              className="absolute -bottom-2 -left-2 w-4 h-4 bg-blue-500 rounded-full cursor-sw-resize z-50 pointer-events-auto shadow-lg hover:bg-blue-600 transition-colors"
              onMouseDown={(e) => {
                e.stopPropagation();
                handleMouseDown(e, element.id, 'resize', 'sw');
              }}
            />
            <div
              className="absolute -bottom-2 -right-2 w-4 h-4 bg-blue-500 rounded-full cursor-se-resize z-50 pointer-events-auto shadow-lg hover:bg-blue-600 transition-colors"
              onMouseDown={(e) => {
                e.stopPropagation();
                handleMouseDown(e, element.id, 'resize', 'se');
              }}
            />
            {/* 변 핸들 */}
            <div
              className="absolute -top-2 left-1/2 transform -translate-x-1/2 w-4 h-4 bg-blue-500 rounded-full cursor-n-resize z-50 pointer-events-auto shadow-lg hover:bg-blue-600 transition-colors"
              onMouseDown={(e) => {
                e.stopPropagation();
                handleMouseDown(e, element.id, 'resize', 'n');
              }}
            />
            <div
              className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-4 h-4 bg-blue-500 rounded-full cursor-s-resize z-50 pointer-events-auto shadow-lg hover:bg-blue-600 transition-colors"
              onMouseDown={(e) => {
                e.stopPropagation();
                handleMouseDown(e, element.id, 'resize', 's');
              }}
            />
            <div
              className="absolute top-1/2 -left-2 transform -translate-y-1/2 w-4 h-4 bg-blue-500 rounded-full cursor-w-resize z-50 pointer-events-auto shadow-lg hover:bg-blue-600 transition-colors"
              onMouseDown={(e) => {
                e.stopPropagation();
                handleMouseDown(e, element.id, 'resize', 'w');
              }}
            />
            <div
              className="absolute top-1/2 -right-2 transform -translate-y-1/2 w-4 h-4 bg-blue-500 rounded-full cursor-e-resize z-50 pointer-events-auto shadow-lg hover:bg-blue-600 transition-colors"
              onMouseDown={(e) => {
                e.stopPropagation();
                handleMouseDown(e, element.id, 'resize', 'e');
              }}
            />

            {/* 회전 핸들 */}
            <div
              className="absolute -top-8 left-1/2 transform -translate-x-1/2 w-5 h-5 bg-green-500 rounded-full cursor-grab z-50 pointer-events-auto shadow-lg hover:bg-green-600 transition-colors flex items-center justify-center"
              onMouseDown={(e) => {
                e.stopPropagation();
                handleMouseDown(e, element.id, 'rotate');
              }}
            >
              <RotateCw className="w-3 h-3 text-white" />
            </div>

          </div>
        )}

        {/* 요소 콘텐츠 */}
        {element.type === 'text' ? (
          isEditing ? (
            <textarea
              value={element.content}
              onChange={(e) => onElementUpdate?.(element.id, { content: e.target.value })}
              onBlur={() => onEditingElementChange?.(null)}
              onKeyDown={(e) => {
                // Shift+Enter로 줄바꿈, Enter만 누르면 편집 종료
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  onEditingElementChange?.(null);
                }
                // Escape로 편집 취소
                if (e.key === 'Escape') {
                  onEditingElementChange?.(null);
                }
                // 방향키는 텍스트 내에서 커서 이동 (기본 동작 허용)
                e.stopPropagation();
              }}
              autoFocus
              className="w-full h-full resize-none border-none outline-none bg-white bg-opacity-90 rounded"
              style={{
                fontSize: element.fontSize * textScale,
                fontFamily: themeFont?.fontFamily || element.fontFamily,
                color: element.color,
                fontWeight: element.fontWeight || themeFont?.effects?.fontWeight,
                textAlign: element.textAlign,
                lineHeight: 1.4,
                padding: '8px',
                minHeight: '100%',
                boxSizing: 'border-box',
              }}
            />
          ) : (
            <div
              style={{
                width: '100%',
                height: '100%',
                fontSize: element.fontSize * textScale,
                fontFamily: themeFont?.fontFamily || element.fontFamily,
                color: element.color,
                fontWeight: element.fontWeight || themeFont?.effects?.fontWeight,
                textAlign: element.textAlign,
                lineHeight: 1.4,
                padding: '8px',
                textShadow: (() => {
                  const fontSize = element.fontSize * scale;
                  let borderWidth = 1;
                  let borderOpacity = 1;
                  
                  if (fontSize < 20) {
                    borderWidth = 0.5;
                    borderOpacity = 0.4;
                  } else if (fontSize < 30) {
                    borderWidth = 0.8;
                    borderOpacity = 0.6;
                  } else if (fontSize < 50) {
                    borderWidth = 1;
                    borderOpacity = 0.8;
                  } else if (fontSize < 80) {
                    borderWidth = 1.5;
                    borderOpacity = 0.9;
                  } else {
                    borderWidth = 2;
                    borderOpacity = 1;
                  }
                  
                  const shadows = [];
                  
                  // 슬라이드 레벨 폰트 테두리 효과
                  if (slide.fontBorder === true && theme) {
                    const borderColor = `${theme.primary}${Math.round(borderOpacity * 255).toString(16).padStart(2, '0')}`;
                    shadows.push(`-${borderWidth}px -${borderWidth}px 0 ${borderColor}, ${borderWidth}px -${borderWidth}px 0 ${borderColor}, -${borderWidth}px ${borderWidth}px 0 ${borderColor}, ${borderWidth}px ${borderWidth}px 0 ${borderColor}`);
                  }
                  
                  // 슬라이드 레벨 폰트 광선 효과
                  if (slide.fontGlow === true && theme) {
                    let glowRange1, glowRange2, glowOpacity1, glowOpacity2;
                    
                    if (fontSize < 20) {
                      glowRange1 = 3; glowRange2 = 5;
                      glowOpacity1 = 0.3; glowOpacity2 = 0.2;
                    } else if (fontSize < 30) {
                      glowRange1 = 5; glowRange2 = 8;
                      glowOpacity1 = 0.4; glowOpacity2 = 0.3;
                    } else if (fontSize < 50) {
                      glowRange1 = 8; glowRange2 = 12;
                      glowOpacity1 = 0.5; glowOpacity2 = 0.4;
                    } else if (fontSize < 80) {
                      glowRange1 = 12; glowRange2 = 18;
                      glowOpacity1 = 0.6; glowOpacity2 = 0.5;
                    } else {
                      glowRange1 = 15; glowRange2 = 25;
                      glowOpacity1 = 0.7; glowOpacity2 = 0.6;
                    }
                    
                    const glowColor1 = `${theme.primary}${Math.round(glowOpacity1 * 255).toString(16).padStart(2, '0')}`;
                    const glowColor2 = `${theme.primary}${Math.round(glowOpacity2 * 255).toString(16).padStart(2, '0')}`;
                    
                    shadows.push(`0 0 ${glowRange1}px ${glowColor1}, 0 0 ${glowRange2}px ${glowColor2}`);
                  }
                  
                  // 요소별 glowEffect 확인
                  const glowEffect = (element as any).glowEffect;
                  if (glowEffect) {
                    shadows.push(glowEffect);
                  }
                  
                  // 기본 텍스트 그림자 제거 (완전히 제거)
                  // if (slide.backgroundImage && !slide.fontBorder && !slide.fontGlow && !glowEffect) {
                  //   shadows.push('2px 2px 8px rgba(0,0,0,0.9), 1px 1px 4px rgba(0,0,0,0.8)');
                  // }
                  
                  return shadows.length > 0 ? shadows.join(', ') : 'none';
                })(),
                letterSpacing: themeFont?.effects?.letterSpacing || 'normal',
                background: isSelected && isEditable ? 'rgba(59, 130, 246, 0.1)' : 'transparent',
                borderRadius: '4px',
                display: 'flex',
                alignItems: element.textAlign === 'center' ? 'center' : element.textAlign === 'right' ? 'flex-end' : 'flex-start',
                justifyContent: element.verticalAlign === 'top' ? 'flex-start' : element.verticalAlign === 'bottom' ? 'flex-end' : 'center',
                wordBreak: 'break-word',
                whiteSpace: 'pre-wrap',
                flexDirection: 'column',
              }}
            >
              <div style={{
                // 포스트잇 스타일일 때 내부 콘텐츠를 정면으로 보이도록 역방향 회전하고 크기 조정
                transform: (() => {
                  if (borderStyle?.id === 'postit') {
                    // 글 내용을 기반으로 일관된 랜덤 각도 생성
                    const content = element.content || '';
                    const seed = content.length + content.charCodeAt(0) || 0;
                    const randomAngle = (Math.sin(seed) * 0.5 + 0.5) * 6 - 3; // -3도 ~ +3도
                    return `rotate(${-randomAngle}deg) scale(0.85)`; // 역방향 회전으로 정면 보이게
                  }
                  return 'none';
                })(),
                // 즉석사진 스타일일 때 기존 텍스트 숨기기
                ...(borderStyle?.id === 'polaroid' && (() => {
                  const aspectRatio = (element.width ?? 200) / (element.height ?? 100);
                  const isHorizontal = aspectRatio > 0.8;
                  const isVertical = aspectRatio < 0.45;
                  const isPolaroidStyle = !(isHorizontal || isVertical);
                  return isPolaroidStyle ? { color: 'transparent' } : {};
                })()),
                // 스티커 타입일 때 글자 스타일 적용
                ...(borderStyle?.id === 'polaroid' && (() => {
                  const aspectRatio = (element.width ?? 200) / (element.height ?? 100);
                  const isHorizontal = aspectRatio > 0.8;
                  const isVertical = aspectRatio < 0.45;
                  if (isHorizontal || isVertical) {
                    return {
                      fontWeight: 'bold',
                      textShadow: '3px 3px 12px rgba(0, 0, 0, 0.2), 1px 1px 6px rgba(0, 0, 0, 0.1)',
                    };
                  }
                  return {};
                })()),
              }}>
                {element.content || '텍스트를 입력하세요'}
                {/* 즉석사진 스타일이 적용된 경우에만 글자 단위 박스 배경 */}
                {borderStyle?.id === 'polaroid' && (() => {
                  const aspectRatio = (element.width ?? 200) / (element.height ?? 100);
                  const isHorizontal = aspectRatio > 0.8;
                  const isVertical = aspectRatio < 0.45;
                  const isPolaroidStyle = !(isHorizontal || isVertical); // 즉석사진 스타일인 경우
                  
                  if (!isPolaroidStyle) {return null;}
                  
                  const text = element.content || '텍스트를 입력하세요';
                  const words = text.split(' ');
                  
                  return (
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      pointerEvents: 'none',
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'flex-start',
                      justifyContent: element.textAlign === 'center' ? 'center' : element.textAlign === 'right' ? 'flex-end' : 'flex-start',
                      lineHeight: 0.4,
                      gap: '1px',
                      alignContent: 'flex-start',
                      paddingTop: '10px',
                    }}>
                      {words.map((word, index) => {
                        // 단어와 인덱스를 기반으로 일관된 랜덤 값 생성
                        const seed = word.length + index * 7;
                        const random1 = Math.sin(seed) * 0.5 + 0.5; // 0~1
                        const random2 = Math.sin(seed * 2) * 0.5 + 0.5; // 0~1
                        // const random3 = Math.sin(seed * 3) * 0.5 + 0.5; // 0~1
                        
                        return (
                          <span
                            key={index}
                            style={{
                              backgroundColor: (() => {
                                // 각 단어마다 다른 배경색 적용
                                const colors = [
                                  'rgba(255, 255, 255, 0.95)',
                                  'rgba(255, 248, 220, 0.9)', // 노란빛
                                  'rgba(255, 240, 245, 0.9)', // 분홍빛
                                  'rgba(240, 248, 255, 0.9)', // 하늘빛
                                  'rgba(255, 250, 240, 0.9)', // 베이지
                                ];
                                return colors[index % colors.length];
                              })(),
                              padding: '1px 4px',
                              margin: '0px',
                              borderRadius: '2px',
                              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.15), 0 1px 2px rgba(0, 0, 0, 0.2)',
                              border: '1px solid rgba(0, 0, 0, 0.1)',
                              // 더 오돌토돌한 찢긴 느낌의 clipPath
                              clipPath: (() => {
                                const points = [];
                                // 각 단어마다 다른 찢긴 패턴
                                const seed = index * 13;
                                
                                // 상단 오돌토돌 (20개 점)
                                for (let i = 0; i <= 20; i++) {
                                  const x = (i / 20) * 100;
                                  const y = Math.sin((i + seed) * 0.8) * 3 + Math.sin((i + seed) * 0.3) * 1;
                                  points.push(`${x}% ${y}%`);
                                }
                                // 우측 오돌토돌 (15개 점)
                                for (let i = 0; i <= 15; i++) {
                                  const y = (i / 15) * 100;
                                  const x = 100 - Math.sin((i + seed + 5) * 0.6) * 2;
                                  points.push(`${x}% ${y}%`);
                                }
                                // 하단 오돌토돌 (20개 점)
                                for (let i = 20; i >= 0; i--) {
                                  const x = (i / 20) * 100;
                                  const y = 100 - Math.sin((i + seed + 10) * 0.8) * 3 - Math.sin((i + seed + 10) * 0.3) * 1;
                                  points.push(`${x}% ${y}%`);
                                }
                                // 좌측 오돌토돌 (15개 점)
                                for (let i = 15; i >= 0; i--) {
                                  const y = (i / 15) * 100;
                                  const x = Math.sin((i + seed + 15) * 0.6) * 2;
                                  points.push(`${x}% ${y}%`);
                                }
                                
                                return `polygon(${points.join(', ')})`;
                              })(),
                              fontSize: (() => {
                                // 기본 폰트 크기에 -15% ~ +25% 랜덤 적용 (기본 크기 증가)
                                const baseSize = (element.fontSize * scale) * 1.3; // 기본 크기 30% 증가
                                const randomFactor = 0.85 + (random1 * 0.4); // 0.85 ~ 1.25 (최대 25% 변동)
                                return baseSize * randomFactor;
                              })(),
                              fontFamily: themeFont?.fontFamily || element.fontFamily || 'Arial, sans-serif',
                              color: (() => {
                                // 각 단어마다 다른 글씨 색 적용
                                const textColors = [
                                  '#2C3E50', // 진한 회색
                                  '#8B4513', // 갈색
                                  '#8B008B', // 진한 보라
                                  '#006400', // 진한 초록
                                  '#8B0000', // 진한 빨강
                                ];
                                return textColors[index % textColors.length];
                              })(),
                              fontWeight: element.fontWeight || themeFont?.effects?.fontWeight,
                              lineHeight: 1.0,
                              whiteSpace: 'pre',
                              // 찢긴 느낌의 그림자 효과
                              filter: (() => {
                                // 랜덤 질감 필터 적용
                                const filters = [
                                  'drop-shadow(1px 1px 2px rgba(0, 0, 0, 0.3))',
                                  'drop-shadow(1px 1px 2px rgba(0, 0, 0, 0.3)) sepia(0.1)',
                                  'drop-shadow(1px 1px 2px rgba(0, 0, 0, 0.3)) contrast(1.1)',
                                  'drop-shadow(1px 1px 2px rgba(0, 0, 0, 0.3)) brightness(1.05)',
                                  'drop-shadow(1px 1px 2px rgba(0, 0, 0, 0.3)) saturate(1.2)',
                                ];
                                return filters[index % filters.length];
                              })(),
                              // 좌우로 5도 정도 랜덤 회전
                              transform: `rotate(${(random2 - 0.5) * 10}deg)`, // -5도 ~ +5도
                            }}
                          >
                            {word}
                          </span>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
              {/* 제목 요소인 경우 키워드 표시 */}
              {element.type === 'text' && 
               element.y !== undefined && 
               element.y < 0.2 && 
               element.fontSize > 20 && 
               slide.keywords && 
               slide.keywords.length > 0 && (
                <div className="mt-2 flex gap-1 flex-wrap" style={{
                  // 포스트잇 스타일일 때 키워드도 정면으로 보이도록 역방향 회전하고 크기 조정
                  transform: borderStyle?.id === 'postit' ? 'rotate(1deg) scale(0.85)' : 'none',
                }}>
                  {slide.keywords.map((keyword, idx) => (
                    <button
                      key={idx}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleKeywordEdit();
                      }}
                      className="px-2 py-1 text-xs bg-blue-100 hover:bg-blue-200 text-blue-800 rounded-full border border-blue-300 transition-colors"
                      title={`키워드: ${keyword}`}
                    >
                      {keyword}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )
        ) : element.type === 'image' && element.content ? (
          <img
            src={element.content}
            alt="Slide element"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              borderRadius: '4px',
              border: isSelected && isEditable ? '2px solid #3B82F6' : '1px solid #E5E7EB',
            }}
            draggable={false}
          />
        ) : element.type === 'video' && element.content ? (
          <video
            src={element.content}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              borderRadius: '4px',
              border: isSelected && isEditable ? '2px solid #3B82F6' : '1px solid #E5E7EB',
              backgroundColor: 'transparent',
            }}
            controls
            preload="metadata"
            playsInline
          />
        ) : element.type === 'shape' ? (
          (() => {
            const textureKey = `${element.id}-${element.backgroundPattern}-${element.backgroundColor}`;
            const hasCustomTexture = effectTextures[textureKey];
            
            const shapeStyle = {
              width: '100%',
              height: '100%',
              background: hasCustomTexture 
                ? `url(${hasCustomTexture})`
                : element.backgroundPattern === 'mosaic' 
                ? `repeating-linear-gradient(45deg, ${element.backgroundColor || '#3B82F6'}, ${element.backgroundColor || '#3B82F6'} 10px, transparent 10px, transparent 20px), repeating-linear-gradient(-45deg, ${element.backgroundColor || '#3B82F6'}, ${element.backgroundColor || '#3B82F6'} 10px, transparent 10px, transparent 20px)`
                : element.backgroundPattern === 'sparkle'
                ? `radial-gradient(circle at 20% 20%, ${element.backgroundColor || '#3B82F6'} 0%, transparent 30%), radial-gradient(circle at 80% 80%, ${element.backgroundColor || '#3B82F6'} 0%, transparent 30%), radial-gradient(circle at 40% 60%, ${element.backgroundColor || '#3B82F6'} 0%, transparent 20%), radial-gradient(circle at 60% 40%, ${element.backgroundColor || '#3B82F6'} 0%, transparent 20%)`
                : element.backgroundPattern === 'blur'
                ? `radial-gradient(circle, ${element.backgroundColor || '#3B82F6'} 0%, ${element.backgroundColor || '#3B82F6'}80 50%, ${element.backgroundColor || '#3B82F6'}20 100%)`
                : (element.backgroundColor || '#3B82F6'),
              backgroundSize: hasCustomTexture ? 'cover' : undefined,
              backgroundRepeat: hasCustomTexture ? 'no-repeat' : undefined,
              border: element.borderColor !== 'transparent' ? `${element.borderWidth || 2}px ${element.borderStyle === 'glow' ? 'solid' : element.borderStyle || 'solid'} ${element.borderColor || '#1E40AF'}` : 'none',
              boxShadow: element.borderStyle === 'glow' && element.borderColor !== 'transparent'
                ? `0 0 10px ${element.borderColor || '#1E40AF'}, 0 0 20px ${element.borderColor || '#1E40AF'}, 0 0 30px ${element.borderColor || '#1E40AF'}`
                : undefined,
              filter: element.backgroundPattern === 'blur' && !hasCustomTexture ? 'blur(2px)' : undefined,
            };

            if (element.shapeType === 'rectangle') {
              return <div style={shapeStyle} />;
            } else if (element.shapeType === 'rounded-rectangle') {
              return <div style={{ ...shapeStyle, borderRadius: '20px' }} />;
            } else if (element.shapeType === 'circle') {
              return <div style={{ ...shapeStyle, borderRadius: '50%' }} />;
            } else if (element.shapeType === 'triangle') {
              return (
                <div style={{ width: '100%', height: '100%', position: 'relative' }}>
                  <div 
                    style={{
                      ...shapeStyle,
                      position: 'absolute',
                      inset: 0,
                      clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)',
                    }}
                  />
                </div>
              );
            } else if (element.shapeType === 'star') {
              return (
                <svg 
                  viewBox="0 0 100 100" 
                  className="w-full h-full"
                  style={{ 
                    overflow: 'visible',
                    filter: element.borderStyle === 'glow' && element.borderColor !== 'transparent' 
                      ? `drop-shadow(0 0 10px ${element.borderColor || '#1E40AF'}) drop-shadow(0 0 20px ${element.borderColor || '#1E40AF'})`
                      : undefined,
                  }}
                >
                  <defs>
                    {hasCustomTexture && (
                      <pattern id={`texture-${element.id}`} x="0" y="0" width="100%" height="100%" patternUnits="objectBoundingBox">
                        <image href={hasCustomTexture} x="0" y="0" width="100%" height="100%" preserveAspectRatio="xMidYMid slice" />
                      </pattern>
                    )}
                    {!hasCustomTexture && element.backgroundPattern === 'mosaic' && (
                      <pattern id={`mosaic-${element.id}`} x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
                        <rect x="0" y="0" width="10" height="10" fill={element.backgroundColor || '#3B82F6'} />
                        <rect x="10" y="10" width="10" height="10" fill={element.backgroundColor || '#3B82F6'} />
                      </pattern>
                    )}
                    {!hasCustomTexture && element.backgroundPattern === 'sparkle' && (
                      <pattern id={`sparkle-${element.id}`} x="0" y="0" width="100" height="100" patternUnits="userSpaceOnUse">
                        <circle cx="20" cy="20" r="10" fill={element.backgroundColor || '#3B82F6'} opacity="0.8" />
                        <circle cx="80" cy="80" r="10" fill={element.backgroundColor || '#3B82F6'} opacity="0.8" />
                        <circle cx="40" cy="60" r="5" fill={element.backgroundColor || '#3B82F6'} opacity="0.6" />
                        <circle cx="60" cy="40" r="5" fill={element.backgroundColor || '#3B82F6'} opacity="0.6" />
                      </pattern>
                    )}
                    {!hasCustomTexture && element.backgroundPattern === 'blur' && (
                      <filter id={`blur-${element.id}`}>
                        <feGaussianBlur in="SourceGraphic" stdDeviation="3"/>
                      </filter>
                    )}
                  </defs>
                  <path 
                    d="M50,0 L61,35 L98,35 L69,57 L80,92 L50,70 L20,92 L31,57 L2,35 L39,35 Z" 
                    fill={hasCustomTexture 
                      ? `url(#texture-${element.id})`
                      : element.backgroundPattern === 'mosaic' 
                      ? `url(#mosaic-${element.id})`
                      : element.backgroundPattern === 'sparkle'
                      ? `url(#sparkle-${element.id})`
                      : element.backgroundColor || '#3B82F6'
                    }
                    stroke={element.borderColor !== 'transparent' ? element.borderColor || '#1E40AF' : 'none'}
                    strokeWidth={element.borderColor !== 'transparent' ? element.borderWidth || 2 : 0}
                    filter={!hasCustomTexture && element.backgroundPattern === 'blur' ? `url(#blur-${element.id})` : undefined}
                  />
                </svg>
              );
            }
            return <div style={shapeStyle} />;
          })()
        ) : element.type === 'div' ? (
          <div
            style={{
              width: '100%',
              height: '100%',
              fontSize: element.fontSize,
              fontFamily: themeFont?.fontFamily || element.fontFamily,
              color: element.color || (theme ? theme.accent : '#000000'), // 테마 3번째 색상으로 텍스트
              fontWeight: element.fontWeight || themeFont?.effects?.fontWeight,
              textAlign: element.textAlign,
              padding: element.padding || 16,
              borderRadius: element.borderRadius || 0,
              boxShadow: element.boxShadow || 'none',
              background: element.hideBackground ? 'transparent' : 
                (() => {
                  // Priority: gradient > backgroundColor with opacity > backgroundColor > theme default
                  const bg = element.gradient || element.backgroundColor || (theme ? theme.secondary + '20' : 'transparent');
                  
                  // Apply opacity only if backgroundOpacity is defined and it's not a gradient
                  if (element.backgroundOpacity !== undefined && bg !== 'transparent' && !element.gradient) {
                    if (bg.startsWith('#')) {
                      const r = parseInt(bg.slice(1, 3), 16);
                      const g = parseInt(bg.slice(3, 5), 16);
                      const b = parseInt(bg.slice(5, 7), 16);
                      return `rgba(${r}, ${g}, ${b}, ${element.backgroundOpacity})`;
                    }
                    // If already rgba or other format, append alpha
                    if (bg.includes('rgba')) {
                      return bg.replace(/,\s*[^,)]+\)/, `, ${element.backgroundOpacity})`);
                    }
                    if (bg.includes('rgb')) {
                      return bg.replace('rgb', 'rgba').replace(')', `, ${element.backgroundOpacity})`);
                    }
                  }
                  return bg;
                })(),
              borderLeft: element.leftBorderHighlight ? 
                `4px solid ${theme?.secondary || '#6366F1'}` : 
                (element.leftBorderWidth ? `${element.leftBorderWidth}px solid ${element.leftBorderColor || '#ccc'}` : 'none'),
              lineHeight: 1.4,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: element.verticalAlign === 'top' ? 'flex-start' : element.verticalAlign === 'bottom' ? 'flex-end' : 'center',
              alignItems: element.textAlign === 'center' ? 'center' : element.textAlign === 'right' ? 'flex-end' : 'flex-start',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              textShadow: (() => {
                let shadow = themeFont?.effects?.textShadow || 'none';
                if (slide.fontGlow && theme) {
                  shadow = `0 0 20px ${theme.primary}, 0 0 30px ${theme.primary}`;
                }
                return shadow;
              })(),
              WebkitTextStroke: slide.fontBorder && theme ? `1px ${theme.secondary}` : 'none',
              letterSpacing: themeFont?.effects?.letterSpacing || 'normal',
            }}
            onDoubleClick={(e) => {
              if (isEditable && !element.locked) {
                e.stopPropagation();
                onEditingElementChange?.(element.id);
              }
            }}
          >
            {isEditing ? (
              <textarea
                value={element.content || ''}
                onChange={(e) => onElementUpdate?.(element.id, { content: e.target.value })}
                onBlur={() => onEditingElementChange?.(null)}
                onKeyDown={(e) => {
                  // Shift+Enter로 줄바꿈, Enter만 누르면 편집 종료
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    onEditingElementChange?.(null);
                  }
                  // Escape로 편집 취소
                  if (e.key === 'Escape') {
                    onEditingElementChange?.(null);
                  }
                  // 방향키는 텍스트 내에서 커서 이동 (기본 동작 허용)
                  e.stopPropagation();
                }}
                autoFocus
                className="w-full h-full resize-none border-none outline-none"
                style={{
                  fontSize: element.fontSize * textScale,
                  fontFamily: themeFont?.fontFamily || element.fontFamily,
                  color: element.color,
                  fontWeight: element.fontWeight || themeFont?.effects?.fontWeight,
                  textAlign: element.textAlign,
                  lineHeight: 1.4,
                  padding: element.padding || '12px',
                  margin: 0,
                  background: 'rgba(255, 255, 255, 0.9)',
                  borderRadius: '4px',
                  minHeight: '100%',
                  boxSizing: 'border-box',
                }}
              />
            ) : (
              element.content || '내용 없음'
            )}
          </div>
        ) : element.type === 'list' ? (
          <div
            style={{
              width: '100%',
              height: '100%',
              fontSize: element.fontSize,
              fontFamily: themeFont?.fontFamily || element.fontFamily,
              color: element.color || (theme ? theme.accent : '#000000'), // 테마 3번째 색상으로 텍스트
              fontWeight: element.fontWeight || themeFont?.effects?.fontWeight,
              padding: element.padding || 12,
              backgroundColor: element.hideBackground ? 'transparent' : 
                (() => {
                  const bgColor = element.backgroundColor || (theme ? theme.secondary + '20' : 'transparent');
                  if (element.backgroundOpacity !== undefined && bgColor !== 'transparent') {
                    // Extract RGB from hex and apply opacity
                    if (bgColor.startsWith('#')) {
                      const r = parseInt(bgColor.slice(1, 3), 16);
                      const g = parseInt(bgColor.slice(3, 5), 16);
                      const b = parseInt(bgColor.slice(5, 7), 16);
                      return `rgba(${r}, ${g}, ${b}, ${element.backgroundOpacity})`;
                    }
                    // If already rgba or other format, append alpha
                    if (bgColor.includes('rgba')) {
                      return bgColor.replace(/,\s*[^,)]+\)/, `, ${element.backgroundOpacity})`);
                    }
                    if (bgColor.includes('rgb')) {
                      return bgColor.replace('rgb', 'rgba').replace(')', `, ${element.backgroundOpacity})`);
                    }
                  }
                  return bgColor;
                })(),
              borderRadius: element.borderRadius || 0,
              boxShadow: element.boxShadow || 'none',
              borderLeft: element.leftBorderHighlight ? 
                `4px solid ${theme?.secondary || '#6366F1'}` : 
                (element.leftBorderWidth ? `${element.leftBorderWidth}px solid ${element.leftBorderColor || '#ccc'}` : 'none'),
              overflow: 'auto',
              textShadow: (() => {
                // 요소별 glowEffect 확인, 없으면 themeFont의 glowEffect 사용
                const glowEffect = (element as any).glowEffect || themeFont?.effects?.glowEffect;
                if (glowEffect) {
                  return glowEffect;
                }
                // 기본 텍스트 그림자
                return themeFont?.effects?.textShadow || 'none';
              })(),
              WebkitTextStroke: (() => {
                // 요소별 textStroke 확인, 없으면 themeFont의 textStroke 사용
                const textStroke = (element as any).textStroke || themeFont?.effects?.textStroke;
                return textStroke && textStroke !== 'none' ? textStroke : 'none';
              })(),
              letterSpacing: themeFont?.effects?.letterSpacing || 'normal',
            }}
            onDoubleClick={(e) => {
              if (isEditable && !element.locked) {
                e.stopPropagation();
                onEditingElementChange?.(element.id);
              }
            }}
          >
            {isEditing ? (
              <textarea
                value={element.listItems?.join('\n') || ''}
                onChange={(e) => {
                  const items = e.target.value.split('\n').filter(item => item.trim());
                  onElementUpdate?.(element.id, { listItems: items });
                }}
                onBlur={() => onEditingElementChange?.(null)}
                onKeyDown={(e) => {
                  // Escape로 편집 취소
                  if (e.key === 'Escape') {
                    onEditingElementChange?.(null);
                  }
                  // 방향키와 Enter는 리스트 편집 시 기본 동작 허용
                  e.stopPropagation();
                }}
                autoFocus
                placeholder="각 줄에 하나씩 항목을 입력하세요"
                className="w-full h-full resize-none border-none outline-none"
                style={{
                  fontSize: element.fontSize * textScale,
                  fontFamily: themeFont?.fontFamily || element.fontFamily,
                  color: element.color,
                  fontWeight: element.fontWeight || themeFont?.effects?.fontWeight,
                  lineHeight: 1.4,
                  padding: element.padding || '12px',
                  margin: 0,
                  background: 'rgba(255, 255, 255, 0.9)',
                  borderRadius: '4px',
                  minHeight: '100%',
                  boxSizing: 'border-box',
                }}
              />
            ) : element.listType === 'number' ? (
              <ol style={{ margin: 0, paddingLeft: '0px' }}>
                {(element.listItems && element.listItems.length > 0 ? element.listItems : (element.content ? element.content.split('\n').filter(item => item.trim()) : ['리스트 항목이 없습니다']))?.map((item, index) => (
                  <li key={index} style={{ marginBottom: '8px', lineHeight: 1.4 }}>
                    {item}
                  </li>
                ))}
              </ol>
            ) : (
              <ul style={{ margin: 0, paddingLeft: '0px', listStyleType: 'none' }}>
                {(element.listItems && element.listItems.length > 0 ? element.listItems : (element.content ? element.content.split('\n').filter(item => item.trim()) : ['리스트 항목이 없습니다']))?.map((item, index) => (
                  <li key={index} style={{ marginBottom: '8px', lineHeight: 1.4, position: 'relative' }}>
                    <span style={{ position: 'absolute', left: '-20px', color: element.color }}>•</span>
                    {item}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : element.type === 'chart' && element.chartData ? (
          <div
            style={{
              width: '100%',
              height: '100%',
              padding: element.padding || 12,
              backgroundColor: element.hideBackground ? 'transparent' : 
                (() => {
                  const bgColor = element.backgroundColor || (theme ? theme.secondary + '10' : 'rgba(255, 255, 255, 0.95)');
                  if (element.backgroundOpacity !== undefined && bgColor !== 'transparent') {
                    // Extract RGB from hex and apply opacity
                    if (bgColor.startsWith('#')) {
                      const r = parseInt(bgColor.slice(1, 3), 16);
                      const g = parseInt(bgColor.slice(3, 5), 16);
                      const b = parseInt(bgColor.slice(5, 7), 16);
                      return `rgba(${r}, ${g}, ${b}, ${element.backgroundOpacity})`;
                    }
                    // If already rgba, replace the alpha value
                    if (bgColor.includes('rgba')) {
                      return bgColor.replace(/,\s*[^,)]+\)/, `, ${element.backgroundOpacity})`);
                    }
                    if (bgColor.includes('rgb')) {
                      return bgColor.replace('rgb', 'rgba').replace(')', `, ${element.backgroundOpacity})`);
                    }
                  }
                  return bgColor;
                })(),
              borderRadius: element.borderRadius || 8,
              boxShadow: element.boxShadow || '0 2px 8px rgba(0,0,0,0.1)',
              borderLeft: element.leftBorderHighlight ? 
                `4px solid ${theme?.secondary || '#6366F1'}` : 'none',
              display: 'flex',
              alignItems: element.verticalAlign === 'top' ? 'flex-start' : element.verticalAlign === 'bottom' ? 'flex-end' : 'center',
              justifyContent: 'center',
            }}
          >
            {element.chartData ? (
              (() => {
                return (
                  <SafeEChartsRenderer
                    key={`chart-${element.id}-${actualWidth}-${actualHeight}`}
                    chartData={element.chartData}
                    width={Math.max(100, actualWidth - (element.padding || 12) * 2)}
                    height={Math.max(100, actualHeight - (element.padding || 12) * 2)}
                    isDarkMode={!theme}
                    theme={theme || { primary: '#3B82F6', secondary: '#1E40AF', accent: '#F59E0B' }}
                  />
                );
              })()
            ) : (
              <div className="flex items-center justify-center text-gray-500">
                No chart data
              </div>
            )}
          </div>
        ) : element.type === 'container' && element.containerItems && element.containerLayout ? (
          <div
            style={{
              width: '100%',
              height: '100%',
              padding: 0,
              backgroundColor: element.backgroundColor || (theme?.accent ? theme.accent + '20' : theme?.quaternary ? theme.quaternary + '20' : '#F59E0B20'), // 테마 3번째 색상 + 투명도
              borderRadius: element.borderRadius || 8,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {(() => {
              const items = element.containerItems;
              const layout = element.containerLayout;
              const containerWidth = actualWidth;
              const containerHeight = actualHeight;
              const itemCount = items.length;
              
              // 컨테이너 비율 확인 (긴 바 타입: 가로가 세로보다 2배 이상 긴 경우)
              const isHorizontalBarType = containerWidth > containerHeight * 2;
              
              // 동적으로 컬럼 수 결정
              let columns = element.containerColumns || 'auto';
              if (columns === 'auto') {
                // 3개 이상이면 긴 바 타입이 아닌 경우 1열로
                if (items.length >= 3 && !isHorizontalBarType) {
                  columns = 1;
                } else {
                  columns = containerWidth < 300 ? 1 : containerWidth < 500 ? 2 : 3;
                }
              }
              
              // 동적 패딩 계산
              const dynamicPadding = layout === 'header-list' || columns === 1 ? 
                                    Math.max(8, Math.min(12, containerHeight * 0.02)) : 
                                    itemCount <= columns ? 
                                    Math.max(6, Math.min(8, containerHeight * 0.015)) : 
                                    (element.padding || 16);
              
              // 사용 가능한 공간 계산 (동적 패딩 제외)
              const contentWidth = containerWidth - dynamicPadding * 2;
              const contentHeight = containerHeight - dynamicPadding * 2;
              
              // 겹침 방지를 위한 엄격한 크기 계산
              let availableHeightPerItem, availableWidthPerItem;
              
              if (layout === 'header-list' || columns === 1) {
                // 세로 배치
                availableHeightPerItem = contentHeight / itemCount;
                availableWidthPerItem = contentWidth;
              } else {
                // 그리드 배치
                const rows = Math.ceil(itemCount / columns);
                availableHeightPerItem = contentHeight / rows;
                availableWidthPerItem = contentWidth / columns;
              }
              
              // 아이콘 크기: 사용 가능한 공간의 30% 이하로 제한
              const maxIconSizeFromHeight = availableHeightPerItem * 0.3;
              const maxIconSizeFromWidth = availableWidthPerItem * 0.15;
              const maxIconSize = Math.min(maxIconSizeFromHeight, maxIconSizeFromWidth);
              const iconSize = Math.max(16, Math.min(48, maxIconSize));
              
              // 레이아웃별 패딩과 간격 최적화
              let basePadding, baseGap;
              
              if (layout === 'header-list' || columns === 1) {
                // 1열 배치: 간격 최소화, 패딩 줄이기
                basePadding = Math.max(6, Math.min(10, availableHeightPerItem * 0.04));
                baseGap = Math.max(3, basePadding * 0.3);
              } else if (itemCount <= columns) {
                // 1행 배치: 좌우 간격 늘리기, 위아래 패딩 줄이기
                basePadding = Math.max(8, Math.min(16, availableWidthPerItem * 0.03));
                baseGap = Math.max(8, Math.min(24, availableWidthPerItem * 0.05));
              } else {
                // 다중 행/열: 기본값
                basePadding = Math.max(4, Math.min(12, Math.min(availableWidthPerItem, availableHeightPerItem) * 0.08));
                baseGap = Math.max(4, basePadding * 0.5);
              }
              
              // 테마 색상 적용
              const textColor = element.color || theme?.secondary || '#1E40AF';
              const themeBackground = theme?.accent || theme?.quaternary || '#F59E0B';
              
              const gridStyle = layout === 'header-list' ? {
                display: 'flex',
                flexDirection: 'column' as const,
                gap: `${baseGap}px`,
                flex: 1,
                alignItems: 'stretch',
                justifyContent: itemCount <= 3 ? 'space-evenly' : 'flex-start',
                width: '100%',
                height: '100%',
              } : layout === 'column-layout' ? {
                display: 'grid',
                gridTemplateColumns: `repeat(${Math.min(itemCount, 3)}, 1fr)`,
                gap: `${baseGap * 1.5}px`,
                alignItems: 'stretch',
                alignContent: 'center',
                justifyItems: 'stretch',
                flex: 1,
                width: '100%',
                height: '100%',
              } : {
                display: 'grid',
                gridTemplateColumns: `repeat(${columns}, 1fr)`,
                gridTemplateRows: `repeat(${Math.ceil(itemCount / columns)}, 1fr)`,
                gap: `${baseGap}px`,
                alignItems: 'center',
                alignContent: 'center',
                justifyItems: 'center',
                flex: 1,
                width: '100%',
                height: '100%',
              };
              
              return (
                <div style={{
                  padding: dynamicPadding,
                  width: '100%',
                  height: '100%',
                  boxSizing: 'border-box',
                  display: 'flex',
                  flexDirection: 'column',
                }}>
                  <div style={gridStyle}>
                    {items.map((item, index) => {
                    switch (layout) {
                      case 'icon-grid':
                        return (
                          <div
                            key={index}
                            className={`flex flex-col items-center text-center rounded-lg transition-transform hover:scale-105`}
                            style={{ 
                              padding: `${basePadding}px`,
                              minHeight: 'fit-content',
                              maxHeight: `${availableHeightPerItem - baseGap}px`,
                              width: '100%',
                              boxSizing: 'border-box',
                              overflow: 'hidden',
                              backgroundColor: isDarkMode ? themeBackground + '20' : themeBackground + '15',
                            }}
                          >
                            <div 
                              style={{ 
                                fontSize: `${iconSize}px`,
                                lineHeight: 1,
                                marginBottom: `${basePadding * 0.3}px`,
                                flexShrink: 0,
                              }}
                            >
                              {item.icon}
                            </div>
                            {item.title && (
                              <div 
                                className="font-bold" 
                                style={{ 
                                  fontSize: Math.min(element.fontSize * textScale, availableHeightPerItem * 0.15),
                                  fontFamily: themeFont?.fontFamily || element.fontFamily || 'Arial, sans-serif',
                                  fontWeight: element.fontWeight || themeFont?.effects?.fontWeight || 'bold',
                                  color: textColor,
                                  lineHeight: 1.1,
                                  marginBottom: `${basePadding * 0.2}px`,
                                  flexShrink: 0,
                                }}
                              >
                                {item.title}
                              </div>
                            )}
                            <div style={{ 
                              fontSize: Math.min(element.fontSize * 0.85 * textScale, availableHeightPerItem * 0.12),
                              fontFamily: themeFont?.fontFamily || element.fontFamily || 'Arial, sans-serif',
                              color: textColor,
                              lineHeight: 1.2,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: 'vertical',
                            }}>
                              {item.content}
                            </div>
                          </div>
                        );
                      case 'header-list':
                        return (
                          <div
                            key={index}
                            className={`flex items-center rounded-md`}
                            style={{ 
                              padding: `${basePadding}px`,
                              gap: `${basePadding}px`,
                              minHeight: 'fit-content',
                              maxHeight: `${availableHeightPerItem - baseGap}px`,
                              width: '100%',
                              boxSizing: 'border-box',
                              overflow: 'hidden',
                              backgroundColor: isDarkMode ? themeBackground + '15' : themeBackground + '10',
                            }}
                          >
                            <div 
                              className="flex-shrink-0"
                              style={{ 
                                fontSize: `${iconSize * 0.8}px`,
                                lineHeight: 1,
                              }}
                            >
                              {item.icon}
                            </div>
                            <div style={{ 
                              fontSize: Math.min(element.fontSize * textScale, availableHeightPerItem * 0.25),
                              fontFamily: themeFont?.fontFamily || element.fontFamily || 'Arial, sans-serif',
                              color: textColor,
                              lineHeight: 1.2,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              flex: 1,
                            }}>
                              {item.content}
                            </div>
                          </div>
                        );
                      case 'card-grid':
                        return (
                          <div
                            key={index}
                            className={`rounded-xl shadow-lg`}
                            style={{ 
                              padding: `${basePadding}px`,
                              minHeight: 'fit-content',
                              maxHeight: `${availableHeightPerItem - baseGap}px`,
                              width: '100%',
                              boxSizing: 'border-box',
                              overflow: 'hidden',
                              display: 'flex',
                              flexDirection: 'column',
                              backgroundColor: isDarkMode ? themeBackground + '25' : themeBackground + '20',
                            }}
                          >
                            <div 
                              style={{ 
                                fontSize: `${iconSize}px`,
                                lineHeight: 1,
                                marginBottom: `${basePadding * 0.3}px`,
                                flexShrink: 0,
                              }}
                            >
                              {item.icon}
                            </div>
                            {item.title && (
                              <div 
                                className="font-bold" 
                                style={{ 
                                  fontSize: Math.min(element.fontSize * textScale, availableHeightPerItem * 0.15),
                                  fontFamily: themeFont?.fontFamily || element.fontFamily || 'Arial, sans-serif',
                                  fontWeight: element.fontWeight || themeFont?.effects?.fontWeight || 'bold',
                                  color: textColor,
                                  lineHeight: 1.1,
                                  marginBottom: `${basePadding * 0.2}px`,
                                  flexShrink: 0,
                                }}
                              >
                                {item.title}
                              </div>
                            )}
                            <div style={{ 
                              fontSize: Math.min(element.fontSize * 0.85 * textScale, availableHeightPerItem * 0.12),
                              fontFamily: themeFont?.fontFamily || element.fontFamily || 'Arial, sans-serif',
                              color: textColor,
                              lineHeight: 1.2,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: 'vertical',
                              flex: 1,
                            }}>
                              {item.content}
                            </div>
                          </div>
                        );
                      case 'column-layout':
                        return (
                          <div
                            key={index}
                            className={`flex flex-col rounded-lg p-4`}
                            style={{
                              backgroundColor: isDarkMode ? themeBackground + '20' : themeBackground + '15',
                              width: '100%',
                              height: '100%',
                              boxSizing: 'border-box',
                              overflow: 'hidden',
                            }}
                          >
                            {/* 아이콘 */}
                            <div 
                              style={{ 
                                fontSize: `${iconSize * 1.2}px`,
                                lineHeight: 1,
                                marginBottom: `${basePadding}px`,
                                textAlign: 'center',
                              }}
                            >
                              {item.icon}
                            </div>
                            {/* 제목 */}
                            {item.title && (
                              <h3 
                                className="font-bold text-center" 
                                style={{ 
                                  fontSize: Math.min(element.fontSize * textScale * 1.1, availableHeightPerItem * 0.2),
                                  fontFamily: themeFont?.fontFamily || element.fontFamily || 'Arial, sans-serif',
                                  fontWeight: element.fontWeight || themeFont?.effects?.fontWeight || 'bold',
                                  color: textColor,
                                  lineHeight: 1.3,
                                  marginBottom: `${basePadding * 0.8}px`,
                                }}
                              >
                                {item.title}
                              </h3>
                            )}
                            {/* 내용 */}
                            <div style={{ 
                              fontSize: Math.min(element.fontSize * 0.9 * textScale, availableHeightPerItem * 0.13),
                              fontFamily: themeFont?.fontFamily || element.fontFamily || 'Arial, sans-serif',
                              color: textColor,
                              lineHeight: 1.4,
                              textAlign: 'center',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 4,
                              WebkitBoxOrient: 'vertical',
                              flex: 1,
                            }}>
                              {item.content}
                            </div>
                          </div>
                        );
                      default:
                        return (
                          <div key={index}>
                            {item.icon} {item.content}
                          </div>
                        );
                    }
                    })}
                  </div>
                </div>
              );
            })()}
          </div>
        ) : (
          <div
            className="w-full h-full border-2 border-dashed border-gray-300 rounded flex items-center justify-center bg-gray-50"
          >
            <p className="text-sm text-gray-500">지원되지 않는 요소 타입: {element.type}</p>
          </div>
        )}
      </div>
    );
  }, [selectedElementId, editingElementId, isEditable, containerSize, slide, borderStyle, handleMouseDown, onElementSelect, onElementDelete, onEditingElementChange, handleKeywordEdit, theme, effectTextures, themeFont, scale, isDarkMode]);

  // Use Grid Layout if enabled (conditional return AFTER all hooks)
  if (slide.useGridLayout) {
    return (
      <SlideGridElements
        slide={slide}
        theme={theme || { primary: '#3B82F6', secondary: '#1E40AF', accent: '#F59E0B', quaternary: '#8B5CF6' }}
        themeFont={themeFont || undefined}
        borderStyle={borderStyle || undefined}
        isEditable={isEditable}
        selectedElementId={selectedElementId}
        editingElementId={editingElementId}
        onElementSelect={onElementSelect}
        onElementUpdate={onElementUpdate}
        onElementDelete={onElementDelete}
        onEditingElementChange={onEditingElementChange}
        onLayoutChange={onLayoutChange}
        containerWidth={containerWidth}
        containerHeight={containerHeight}
        scale={scale}
      />
    );
  }

  return (
    <>
      <div
        className="slide-elements-layer"
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
        }}
      >
        {slide.elements?.map(element => (
          <div key={element.id} style={{ pointerEvents: 'auto' }}>
            {renderElement(element)}
          </div>
        ))}
      </div>
      
      {/* 키워드 편집 모달 */}
      <KeywordEditModal
        isOpen={showKeywordModal}
        keywords={editingKeywords}
        onSave={handleKeywordSave}
        onClose={() => setShowKeywordModal(false)}
        isDarkMode={isDarkMode}
      />
    </>
  );
});

SlideElements.displayName = 'SlideElements'; 
